/**
 * Tenant profile (household) hooks — WP-PLAN-1 Phase 5.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * The tenant profile carries birth year and state of residence, and it was read
 * two different ways:
 *
 *   1. `useBirthYear()` (useRetirementPlanning.ts) — React Query on
 *      `['tenant-profile', tenantId]`, used by the Planning page.
 *   2. `FinancialPlanningSettings` — a `useEffect` calling
 *      `settingsApi.getTenantProfile` straight into local component state,
 *      bypassing React Query entirely.
 *
 * Because (2) never touched the cache, saving a birth year in Settings left
 * (1) serving its cached value for the full 5-minute `staleTime`. The user
 * changed their birth year, walked to Planning, and saw their old age — with no
 * error and nothing to suggest the value was stale. Reloading "fixed" it, which
 * is exactly what makes this kind of bug survive.
 *
 * One module now owns the key for BOTH reading and writing, so every write
 * invalidates the single cache entry every reader shares.
 *
 * The key literal `['tenant-profile', tenantId]` is unchanged, so anything
 * still reading it directly keeps working.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { settingsApi, type TenantProfile } from '@/services/settings';
import { useAuthStore } from '@/store/authStore';

/**
 * The fields `settingsApi.updateTenantProfile` accepts (services/settings.ts:218).
 *
 * Declared here rather than imported: `TenantProfileUpdate` in
 * types/allocation.ts is a DIFFERENT concept (the allocation portfolio profile),
 * and reusing that name would compile while meaning something else entirely.
 */
export interface TenantProfileUpdate {
  birthYear?: number | null;
  stateOfResidence?: string | null;
}

/**
 * Query key factory, matching the convention used by the other hook modules
 * (see `connectionKeys` in useConnections.ts).
 */
export const tenantProfileKeys = {
  all: ['tenant-profile'] as const,
  detail: (tenantId: string | undefined) =>
    ['tenant-profile', tenantId] as const,
};

/** How long a profile stays fresh. Unchanged from the original useBirthYear. */
const TENANT_PROFILE_STALE_TIME = 5 * 60 * 1000;

/**
 * Read the tenant profile.
 *
 * Disabled until a tenantId is known, so it never fires with `undefined`.
 */
export function useTenantProfile() {
  const user = useAuthStore((state) => state.user);
  const tenantId = user?.tenantId;

  const query = useQuery({
    queryKey: tenantProfileKeys.detail(tenantId),
    queryFn: () => settingsApi.getTenantProfile(tenantId!),
    enabled: !!tenantId,
    staleTime: TENANT_PROFILE_STALE_TIME,
  });

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    tenantId,
  };
}

/**
 * Update the tenant profile.
 *
 * Invalidates the shared key on success, which is the whole point of this
 * module: the Planning page picks the change up without a reload.
 *
 * `showToast` defaults to true. FinancialPlanningSettings renders its own
 * inline success and error UI, so it opts out to avoid telling the user twice.
 */
export function useUpdateTenantProfile({ showToast = true } = {}) {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const tenantId = user?.tenantId;

  return useMutation<TenantProfile, unknown, TenantProfileUpdate>({
    mutationFn: (update: TenantProfileUpdate) => {
      if (!tenantId) throw new Error('No tenant ID');
      return settingsApi.updateTenantProfile(tenantId, update);
    },
    onSuccess: (updated) => {
      // Seed the cache with the server's response as well as invalidating, so
      // readers that mount before the refetch resolves already see the new
      // value rather than briefly showing the stale one.
      queryClient.setQueryData(tenantProfileKeys.detail(tenantId), updated);
      queryClient.invalidateQueries({
        queryKey: tenantProfileKeys.detail(tenantId),
      });
      if (showToast) toast.success('Saved');
    },
    onError: (error: unknown) => {
      if (!showToast) return;
      toast.error(extractMessage(error, 'Failed to save profile'));
    },
  });
}

/**
 * Clear the birth year.
 *
 * A separate endpoint because the update endpoint cannot distinguish "leave
 * unchanged" from "set to null".
 */
export function useClearBirthYear({ showToast = true } = {}) {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const tenantId = user?.tenantId;

  return useMutation<TenantProfile, unknown, void>({
    mutationFn: () => {
      if (!tenantId) throw new Error('No tenant ID');
      return settingsApi.clearBirthYear(tenantId);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(tenantProfileKeys.detail(tenantId), updated);
      queryClient.invalidateQueries({
        queryKey: tenantProfileKeys.detail(tenantId),
      });
      if (showToast) toast.success('Birth year cleared');
    },
    onError: (error: unknown) => {
      if (!showToast) return;
      toast.error(extractMessage(error, 'Failed to clear birth year'));
    },
  });
}

/** Matches the error-shape handling already used in useRetirementPlanning.ts. */
function extractMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'detail' in error) {
    return String((error as { detail: unknown }).detail);
  }
  return fallback;
}
